import React from 'react';
import { Avatar, Tooltip } from '@mui/material';

/**
 * The employee's profile picture with a coloured ring showing their status.
 *
 * The ring is drawn with box-shadow rather than a border so it adds no width:
 * it can be dropped into an existing layout without shifting anything.
 */

export const LEVEL_COLORS = {
    gold: '#d9a418',
    silver: '#9aa4b0',
    bronze: '#b5793a',
};

const initialsOf = (name = '', email = '') => {
    const source = (name || '').trim() || (email || '').trim();
    if (!source) return '?';
    const parts = source.split(/\s+/).filter(Boolean);
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const LevelAvatar = ({
    level,
    src,
    name,
    email,
    size = 40,
    showTooltip = true,
    tooltipExtra,
    sx = {},
}) => {
    const color = LEVEL_COLORS[level];
    const ringWidth = Math.max(2, Math.round(size / 13));

    const avatar = (
        <Avatar
            src={src || undefined}
            alt={name || email || 'Profile'}
            sx={{
                width: size,
                height: size,
                fontSize: size / 2.6,
                fontWeight: 600,
                background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
                // Inner white gap, then the level ring
                boxShadow: color
                    ? `0 0 0 ${ringWidth}px #fff, 0 0 0 ${ringWidth * 2}px ${color}`
                    : 'none',
                ...sx,
            }}
        >
            {initialsOf(name, email)}
        </Avatar>
    );

    if (!color || !showTooltip) {
        return avatar;
    }

    const label = level.charAt(0).toUpperCase() + level.slice(1);

    return (
        <Tooltip title={tooltipExtra ? `${label} — ${tooltipExtra}` : label} arrow>
            {avatar}
        </Tooltip>
    );
};

export default LevelAvatar;
